// -----------------------------------------------------------------------------
// Context simples pra compartilhar `aiAutoAttendEnabled` entre o
// `KanbanIaToggle` (header) e o `LeadCard` (cards).
// Quando o toggle global está ON, o botão "Atender com IA" no card é
// redundante e SHALL ser escondido. Cf. `map-funnel-stage-and-add-ia-toggle`.
// -----------------------------------------------------------------------------

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  AutorunUserSummary,
  getAutorunUsers,
  getMyUsuario,
} from '../services/userPreferences';

interface UserPreferencesContextValue {
  aiAutoAttendEnabled: boolean | null;
  setAiAutoAttendEnabled: (next: boolean) => void;
  reload: () => Promise<void>;
  /** Lista de usuários da org em autorun (carregada se o user logado é admin). */
  autorunUsers: AutorunUserSummary[];
  /** Set de usuarioId em autorun pra checagem O(1) — usado pelo badge no card. */
  autorunUserIds: Set<string>;
  reloadAutorunUsers: () => Promise<void>;
}

const UserPreferencesContext = createContext<UserPreferencesContextValue | null>(null);

const ADMIN_ROLE_TOKENS = ['owner', 'admin', 'super_admin'];

function tokenizeRole(role: unknown): string {
  return typeof role === 'string' ? role : '';
}

export function UserPreferencesProvider({ children }: { children: React.ReactNode }) {
  const [aiAutoAttendEnabled, setEnabled] = useState<boolean | null>(null);
  const [autorunUsers, setAutorunUsers] = useState<AutorunUserSummary[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);

  const reload = useCallback(async () => {
    try {
      const u = await getMyUsuario();
      setEnabled(Boolean(u.aiAutoAttendEnabled));
      setIsAdmin(ADMIN_ROLE_TOKENS.includes(tokenizeRole(u.role)));
    } catch (err) {
      console.warn('[UserPreferencesProvider] failed to load preference', err);
      setEnabled(false);
    }
  }, []);

  const reloadAutorunUsers = useCallback(async () => {
    if (!isAdmin) {
      setAutorunUsers([]);
      return;
    }
    try {
      const list = await getAutorunUsers();
      setAutorunUsers(list);
    } catch (err) {
      console.warn('[UserPreferencesProvider] failed to load autorun list', err);
      setAutorunUsers([]);
    }
  }, [isAdmin]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    reloadAutorunUsers();
  }, [reloadAutorunUsers]);

  const autorunUserIds = React.useMemo(
    () => new Set(autorunUsers.map((u) => u.usuarioId)),
    [autorunUsers]
  );

  return (
    <UserPreferencesContext.Provider
      value={{
        aiAutoAttendEnabled,
        setAiAutoAttendEnabled: setEnabled,
        reload,
        autorunUsers,
        autorunUserIds,
        reloadAutorunUsers,
      }}
    >
      {children}
    </UserPreferencesContext.Provider>
  );
}

/**
 * Hook que retorna `aiAutoAttendEnabled` do usuário logado. Retorna `null`
 * enquanto carrega ou se não há provider. Componentes que querem reagir
 * ao estado do toggle SHALL usar este hook.
 */
export function useAiAutoAttend(): boolean | null {
  const ctx = useContext(UserPreferencesContext);
  return ctx?.aiAutoAttendEnabled ?? null;
}

/**
 * Hook completo pro `KanbanIaToggle` — expõe setter e reload em adição
 * ao estado.
 */
export function useUserPreferences(): UserPreferencesContextValue | null {
  return useContext(UserPreferencesContext);
}

/**
 * Hook pra checar se um usuário (geralmente `lead.atendenteId`) está em
 * modo autorun. Retorna `false` se não há provider ou se o usuário
 * logado não é admin (sem visibilidade da lista).
 */
export function useIsAutorunUser(usuarioId: string | undefined): boolean {
  const ctx = useContext(UserPreferencesContext);
  if (!ctx || !usuarioId) return false;
  return ctx.autorunUserIds.has(usuarioId);
}

/**
 * Hook pro menu flutuante admin — retorna lista completa de users em
 * autorun (vazia se não-admin) + função pra recarregar.
 */
export function useAutorunUsers(): {
  users: AutorunUserSummary[];
  reload: () => Promise<void>;
} {
  const ctx = useContext(UserPreferencesContext);
  return {
    users: ctx?.autorunUsers ?? [],
    reload: ctx?.reloadAutorunUsers ?? (async () => {}),
  };
}
